import { z } from "zod";

// How Clevis invoices land in a company's QuickBooks (Online or Desktop) so
// they look like the invoices the company already makes. Stored per company
// on integrationConfigs/{quickbooks-online|quickbooks-desktop}.syncProfile.
// "Learn from my QuickBooks" (qbLearn) proposes one from the company's own
// recent invoices; nothing changes until an admin applies it.

/** A Clevis line whose description contains `contains` bills to that QuickBooks item. */
export const QbItemRuleSchema = z.object({
  contains: z.string().trim().toLowerCase().min(2).max(60),
  itemName: z.string().min(1).max(200),
  /** QuickBooks Online Id; Desktop references items by FullName (itemName). */
  itemId: z.string().nullable().default(null),
});
export type QbItemRule = z.infer<typeof QbItemRuleSchema>;

export const QbPoPlacementSchema = z.enum([
  "none",
  /** Prepend "PO: …" to the customer-facing memo. */
  "memo",
  /** QuickBooks Desktop's own PO Number field. */
  "poNumber",
  /** A QuickBooks Online sales-form custom field (e.g. "P.O. Number"). */
  "customField",
]);
export type QbPoPlacement = z.infer<typeof QbPoPlacementSchema>;

export const QbSyncProfileSchema = z.object({
  // "generic": every line → default item. "matchByName": a line whose
  // description equals an item name uses it. "rules": keyword rules first,
  // then name match, then the default.
  itemStrategy: z.enum(["generic", "matchByName", "rules"]).default("generic"),
  defaultItemId: z.string().nullable().default(null),
  defaultItemName: z.string().nullable().default(null),
  itemRules: z.array(QbItemRuleSchema).max(50).default([]),
  classId: z.string().nullable().default(null),
  className: z.string().nullable().default(null),
  taxCodeId: z.string().nullable().default(null),
  taxCodeName: z.string().nullable().default(null),
  termsId: z.string().nullable().default(null),
  termsName: z.string().nullable().default(null),
  poPlacement: QbPoPlacementSchema.default("none"),
  poCustomFieldId: z.string().nullable().default(null),
  poCustomFieldName: z.string().nullable().default(null),
});
export type QbSyncProfile = z.infer<typeof QbSyncProfileSchema>;

/** The learn run, on the same config doc (`learn`). */
export const QbLearnSchema = z.object({
  status: z.enum(["waiting_for_quickbooks", "analyzing", "ready", "applied", "error"]),
  proposed: QbSyncProfileSchema.nullable().default(null),
  /** One plain sentence per setting: why the AI chose it. */
  reasons: z.record(z.string()).default({}),
  summary: z.string().default(""),
  sampledInvoices: z.number().int().default(0),
  error: z.string().nullable().default(null),
  chainId: z.string().nullable().default(null),
  requestedAt: z.number(),
  finishedAt: z.number().nullable().default(null),
});
export type QbLearn = z.infer<typeof QbLearnSchema>;
